/**
 * Keyboard highlight state for the AI search result listbox.
 *
 * graft/A6: extracted verbatim from AiSearch.tsx. Owns the highlighted index,
 * the key-based relocation that keeps a selection glued to its hit across
 * commits and appends, and the scroll-into-view effect. Surface-agnostic: the
 * dropdown and the routed page both render one listbox of `navigableResults`
 * with the same option ids.
 *
 * The key event switch itself stays with the surface — Escape means "close the
 * panel" in one and something else in the other.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { OPTION_ID_PREFIX } from "../AiSearchResults";
import { getHitKey } from "../entityMeta";
import type { ICategorizedHit } from "../types";

interface IUseAiSearchKeyboardArgs {
    /** The full visible list in painted order — committed rows then appended. */
    navigableResults: ICategorizedHit[];
    searchId: string;
    /** PR-A1: true once the one-time commit has published the buffered list. */
    committed: boolean;
}

export interface IAiSearchKeyboard {
    highlightedIndex: number;
    /** Move the highlight down one row, wrapping to the top. */
    moveDown: () => void;
    /** Move the highlight up one row, wrapping to the bottom. */
    moveUp: () => void;
    /** Drop the highlight entirely (new input, close, post-click). */
    clearHighlight: () => void;
    /** `aria-activedescendant` for the combobox input, or undefined when nothing is highlighted. */
    activeDescendant: string | undefined;
}

export function useAiSearchKeyboard(args: IUseAiSearchKeyboardArgs): IAiSearchKeyboard {
    const { navigableResults, searchId, committed } = args;

    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    // PR-A2 (ADO 286302): the KEY of the keyboard-highlighted hit. The index
    // is a position in a list that commits/appends/re-fuses under the cursor;
    // the key identifies the HIT, so the selection can follow it to its new
    // position instead of evaporating whenever the count changes.
    const highlightedKeyRef = useRef<string | null>(null);
    // A2-review (thread 1511894): the last COMMITTED navigable list, read
    // from inside the setHighlightedIndex functional updater instead of the
    // handler's closure. The `prev` index the updater receives is relative
    // to the list the user was looking at when they pressed the key (the
    // last committed render — the same commit whose DOM handler fired), and
    // this ref is synced in an effect on that same commit, so the index→hit
    // pairing stays consistent even if React flushes the updater against a
    // fresher closure (e.g. a result event landing between the keydown and
    // the flush). The relocation effect then carries the recorded key into
    // whatever list commits next.
    const navigableResultsRef = useRef<ICategorizedHit[]>([]);

    // Functional updater (rapid keydowns before a re-render see the real
    // previous index); recording the key in a ref is idempotent, so the
    // updater stays safe under double invocation. The list is read through
    // navigableResultsRef — synced to the committed render — not a closure,
    // so the flush-time index can never be paired with a mismatched array
    // (A2 review thread 1511894).
    const move = useCallback((step: (prev: number, listCount: number) => number) => {
        setHighlightedIndex((prev) => {
            const list = navigableResultsRef.current;
            if (list.length === 0) return prev;
            const next = Math.min(Math.max(step(prev, list.length), 0), list.length - 1);
            highlightedKeyRef.current = getHitKey(list[next]);
            return next;
        });
    }, []);

    // Wrap bounds come from the ref inside `move`, so a list that changed size
    // between keydown and flush cannot produce an out-of-range index.
    const moveDown = useCallback(() => {
        move((prev, listCount) => (prev < listCount - 1 ? prev + 1 : 0));
    }, [move]);

    const moveUp = useCallback(() => {
        move((prev, listCount) => (prev > 0 ? prev - 1 : listCount - 1));
    }, [move]);

    const clearHighlight = useCallback(() => {
        highlightedKeyRef.current = null;
        setHighlightedIndex(-1);
    }, []);

    // Scroll highlighted result into view
    useEffect(() => {
        if (highlightedIndex < 0) return;
        const el = document.getElementById(`${OPTION_ID_PREFIX}${highlightedIndex}`);
        if (el) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    // Reset the keyboard highlight when the list's IDENTITY changes (A1
    // review 1511769), in BOTH flag states: a new search (searchId) and the
    // stable path's one-time commit (committed false→true, when the buffered
    // rows replace whatever the index pointed at; always false flag-off).
    // PR-A2 merge: the recorded KEY clears in lockstep with the index so the
    // relocation effect below can't resurrect a previous search's selection
    // when the new stream happens to contain the same hit.
    useEffect(() => {
        highlightedKeyRef.current = null;
        setHighlightedIndex(-1);
    }, [searchId, committed]);

    // PR-A2 (ADO 286302, both flag states): when the visible list changes
    // WITHIN a search, RELOCATE the keyboard selection by hit key instead of
    // wiping it. This replaces the pre-A2 legacy reset-on-resultCount effect
    // (flipped deliberately with its T4 baselines): the cursor follows the
    // hit it was on — to its new index if the row moved (legacy per-event
    // re-fuses), unchanged if the row didn't (stable-path appends), and
    // cleared only when the highlighted hit itself left the list. Identity
    // changes (new search / commit) are handled by the reset above, which
    // runs first in declaration order.
    useEffect(() => {
        // Sync the committed-list ref FIRST so a keydown arriving after this
        // render pairs its index with this exact list (thread 1511894).
        navigableResultsRef.current = navigableResults;
        const key = highlightedKeyRef.current;
        if (key === null) return;
        const nextIndex = navigableResults.findIndex((hit) => getHitKey(hit) === key);
        if (nextIndex === -1) {
            // The highlighted hit left the list: clear key and index in
            // LOCKSTEP so the next ArrowDown starts from the top instead of
            // resuming from a stale index into a different row.
            highlightedKeyRef.current = null;
            setHighlightedIndex(-1);
        } else {
            // No-op setState when the index is unchanged: React bails out.
            setHighlightedIndex(nextIndex);
        }
    }, [navigableResults]);

    const activeDescendant =
        highlightedIndex >= 0 ? `${OPTION_ID_PREFIX}${highlightedIndex}` : undefined;

    return { highlightedIndex, moveDown, moveUp, clearHighlight, activeDescendant };
}
